<template>
  <!-- Teleport vers <body> : ce composant peut être monté dans des conteneurs
       avec overflow:hidden (ex. layout POS, plein écran mobile) qui rognent
       les éléments position:fixed — le rendu direct sous <body> évite ça.
       Visibilité gérée par Vue (v-if), pas par bootstrap.Modal : ça évite
       toute dépendance à l'état/CSS de Bootstrap dans un contexte (mobile,
       PWA plein écran) où le modal s'avérait invisible sans erreur visible. -->
  <teleport to="body">
  <div v-if="isOpen" class="ticket-modal-overlay" @click.self="close">
    <div class="ticket-modal-dialog">
      <form @submit.prevent="submitTicket">
        <div class="ticket-modal-content">
          <div class="ticket-modal-header">
            <h5 class="ticket-modal-title">{{ $t('support.new_ticket_title') }}</h5>
            <button type="button" class="btn-close" @click="close"></button>
          </div>
          <div class="ticket-modal-body">
            <div class="mb-4">
              <label class="form-label fw-bold">{{ $t('support.category_label') }}</label>
              <select
                ref="categorySelect"
                v-model="form.category"
                data-control="select2"
                data-search="true"
                data-placeholder="Filter"
                class="form-select form-select-solid"
                :class="selectClass"
              >
                <option value="assistance">{{ $t('support.category_assistance') }}</option>
                <option value="incident">{{ $t('support.category_incident') }}</option>
                <option value="bug">{{ $t('support.category_bug') }}</option>
                <option value="improvement">{{ $t('support.category_improvement') }}</option>
                <option value="installation">{{ $t('support.category_installation') }}</option>
                <option value="configuration">{{ $t('support.category_configuration') }}</option>
                <option value="billing">{{ $t('support.category_billing') }}</option>
                <option value="account">{{ $t('support.category_account') }}</option>
                <option value="security">{{ $t('support.category_security') }}</option>
                <option value="other">{{ $t('support.category_other') }}</option>
              </select>
            </div>

            <div class="mb-4">
              <label class="form-label fw-bold">{{ $t('support.priority_label') }}</label>
              <div class="d-flex gap-2">
                <button
                  v-for="level in priorityLevels"
                  :key="level"
                  type="button"
                  class="btn btn-sm flex-fill"
                  :class="form.priority === level ? priorityActiveClass(level) : 'btn-light'"
                  @click="form.priority = level"
                >{{ $t('support.priority_' + level) }}</button>
              </div>
            </div>

            <div class="mb-4">
              <label class="form-label fw-bold">{{ $t('support.message_label') }}</label>
              <div ref="messageEditor"></div>
              <div
                class="support-dropzone mt-2"
                :class="{ 'support-dropzone--active': isDragOver }"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="onImageDrop"
                @click="$refs.imageFileInput.click()"
              >
                <span v-if="uploadingImage">
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  {{ $t('support.dropzone_uploading') }}
                </span>
                <span v-else>
                  <i class="bi bi-image me-2"></i>
                  {{ $t('support.dropzone_label') }}
                </span>
                <input ref="imageFileInput" type="file" accept="image/*" class="d-none" @change="onImageFileSelected" />
              </div>

              <div v-if="attachedImages.length" class="support-attachments mt-3">
                <div v-for="(img, idx) in attachedImages" :key="img.url" class="support-attachment-tile">
                  <img :src="img.url" alt="attachment" />
                  <button type="button" class="support-attachment-remove" @click="removeAttachedImage(idx)">
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="ticket-modal-footer">
            <button type="button" @click="close" class="btn btn-light">{{ $t('support.cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="isSending || !form.message.trim()">
              <span v-if="isSending" class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('support.send') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
  </teleport>
</template>

<script>
import { markRaw } from 'vue'

export default {
  name: 'CreateTicketModal',
  props: {
    // Utile si jamais plusieurs instances coexistent sur la même page — sert
    // uniquement de préfixe pour d'éventuels styles ciblés, plus d'id DOM à
    // faire correspondre (la visibilité n'est plus gérée par Bootstrap).
    modalId: { type: String, default: 'createTicketModal' },
    selectClass: { type: String, default: '' },
  },
  emits: ['sent'],
  data() {
    return {
      isOpen: false,
      isSending: false,
      messageEditor: null,
      isDragOver: false,
      uploadingImage: false,
      attachedImages: [],
      priorityLevels: ['low', 'normal', 'high', 'critical'],
      form: {
        category: 'assistance',
        priority: 'normal',
        message: ''
      }
    }
  },
  beforeUnmount() {
    if (this.messageEditor) this.messageEditor.destroy()
  },
  methods: {
    // Le <select> et l'éditeur n'existent dans le DOM que quand isOpen est
    // vrai (v-if) — on les (ré)initialise à chaque ouverture plutôt qu'une
    // seule fois au montage.
    async initEditor() {
      if (this.messageEditor) {
        this.messageEditor.destroy()
        this.messageEditor = null
      }
      if (!window.ClassicEditor || !this.$refs.messageEditor) return
      const editor = await window.ClassicEditor.create(this.$refs.messageEditor, {
        toolbar: [
          'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList',
          '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo'
        ]
      })
      this.messageEditor = markRaw(editor)
      this.messageEditor.model.document.on('change:data', () => {
        this.form.message = this.messageEditor.getData()
      })
    },

    initSelect() {
      if (!this.$refs.categorySelect) return
      const sel = $(this.$refs.categorySelect)
      // dropdownParent : sans ça, select2 ouvre sa liste directement dans
      // <body> avec son propre z-index par défaut, qui peut passer sous la
      // superposition de ce modal (z-index élevé pour rester visible dans
      // le layout POS) — on la rattache donc explicitement à la boîte du
      // modal pour qu'elle hérite du bon contexte d'empilement.
      sel.select2({ dropdownParent: sel.closest('.ticket-modal-content') })
      sel.on('change', (e) => {
        this.form.category = e.target.value
      })
    },

    onImageDrop(e) {
      this.isDragOver = false
      const file = e.dataTransfer.files[0]
      if (file) this.uploadAndAttachImage(file)
    },

    onImageFileSelected(e) {
      const file = e.target.files[0]
      if (file) this.uploadAndAttachImage(file)
      e.target.value = ''
    },

    uploadAndAttachImage(file) {
      if (!file.type.startsWith('image/')) return
      this.uploadingImage = true
      const formData = new FormData()
      formData.append('upload', file)
      axios.post('support-tickets/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then((res) => {
        this.attachedImages.push({ url: res.data.url })
      }).catch(() => {
        window.toastr['error'](this.$t('support.image_upload_error'))
      }).finally(() => {
        this.uploadingImage = false
      })
    },

    removeAttachedImage(index) {
      this.attachedImages.splice(index, 1)
    },

    priorityActiveClass(priority) {
      return {
        low: 'btn-secondary',
        normal: 'btn-primary',
        high: 'btn-dark',
        critical: 'btn-danger'
      }[priority] || 'btn-primary'
    },

    // preset facultatif : ticket pré-rempli (ex. { category: 'billing', message: '<p>…</p>' })
    open(preset = {}) {
      if (preset.category) this.form.category = preset.category
      this.isOpen = true
      this.$nextTick(async () => {
        await this.initEditor()
        this.initSelect()
        if (preset.message) {
          this.messageEditor?.setData(preset.message)
          this.form.message = preset.message
        }
      })
    },

    close() {
      this.isOpen = false
    },

    submitTicket() {
      if (!this.form.message.trim()) return
      this.isSending = true
      const payload = {
        ...this.form,
        images: this.attachedImages.map(img => img.url)
      }
      axios.post('support-tickets', payload).then((res) => {
        window.toastr['success'](this.$t('support.sent_success'))
        this.form.message = ''
        if (this.messageEditor) this.messageEditor.setData('')
        this.form.category = 'assistance'
        this.form.priority = 'normal'
        this.attachedImages = []
        this.close()
        this.$emit('sent', res.data)
      }).catch(() => {
        window.toastr['error'](this.$t('support.sent_error'))
      }).finally(() => {
        this.isSending = false
      })
    },
  }
}
</script>

<style scoped>
.ticket-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10500;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  overflow-y: auto;
}

.ticket-modal-dialog {
  width: 100%;
  max-width: 800px;
  margin: auto;
}

.ticket-modal-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.ticket-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #eff2f5;
}

.ticket-modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.ticket-modal-body {
  padding: 1.25rem;
  overflow-y: auto;
}

.ticket-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 1rem 1.25rem;
  border-top: 1px solid #eff2f5;
}

.support-dropzone {
  border: 2px dashed #d5d9e0;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  font-size: 0.85rem;
  color: #6c7293;
  cursor: pointer;
  transition: all 0.15s ease;
}

.support-dropzone:hover {
  border-color: var(--kt-primary);
  color: var(--kt-primary);
}

.support-dropzone--active {
  border-color: var(--kt-primary);
  background: var(--kt-primary-light, rgba(0, 158, 247, 0.1));
  color: var(--kt-primary);
}

.support-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.support-attachment-tile {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e4e6ef;
}

.support-attachment-tile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.support-attachment-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  padding: 0;
  line-height: 1;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  cursor: pointer;
}

.support-attachment-remove:hover {
  background: var(--kt-danger, #f1416c);
}
</style>
